// src/components/DetallePlanDrawer.js
//
// Super-drawer reusable: muestra TODO el ciclo de un plan (de un certificado
// DGR) en una sola vista. Usa el endpoint /dgr/pasaje/detalle-completo/:id.
//
// Pensado para resolver "no sé dónde mirar para X" — en lugar de tener que
// abrir 3 pantallas distintas para entender qué pasó con un plan, este drawer
// muestra el ciclo end-to-end:
//   Importación DGR → Conciliación BSJ/RA → Vinculación expediente → Auditoría → Cobranza
//
// Se monta como Drawer "anchor=right" con su propio header. El padre solo
// pasa idDetalle y onClose.

import React, { useEffect, useState } from 'react';
import {
    Drawer, Box, Typography, IconButton, CircularProgress, Alert, Paper,
    Chip, Divider, Stack, Tooltip, Button,
} from '@mui/material';
import {
    Close as CloseIcon,
    Inbox as ImportIcon,
    AccountBalance as ConciliacionIcon,
    Link as VinculacionIcon,
    FactCheck as AuditoriaIcon,
    Payments as CobranzaIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    ErrorOutline as ErrorIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { dgrPasajeDetalleCompletoJWT } from '../api/endpoints';

const COLOR = '#1565c0';
const BR = 4;

const APP_BAR_TOP = { xs: '56px', sm: '64px' };
const APP_BAR_HEIGHT_CALC = { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' };

const ESTADO_CONC = {
    V: { label: 'Conciliado', color: '#2e7d32', bg: '#E8F5E9' },
    X: { label: 'Sin Coincidencia', color: '#c62828', bg: '#FFEBEE' },
    S: { label: 'Sin Pagos', color: '#e65100', bg: '#FFF3E0' },
    W: { label: 'Sin Solicitud DGR', color: '#0277bd', bg: '#E3F2FD' },
    D: { label: 'Duplicado', color: '#616161', bg: '#EEEEEE' },
};

function fmtMonto(n) {
    return Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtFecha(d) {
    if (!d) return '—';
    const s = String(d);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    try { return new Date(d).toLocaleDateString('es-AR'); } catch { return s; }
}

export default function DetallePlanDrawer({ open, idDetalle, onClose }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [d, setD] = useState(null);

    useEffect(() => {
        if (!open || !idDetalle) return;
        setLoading(true); setError(null); setD(null);
        dgrPasajeDetalleCompletoJWT(idDetalle)
            .then(r => {
                if (r?.ok) setD(r.detalle);
                else setError(r?.mensaje || 'No se pudo cargar el detalle');
            })
            .catch(e => setError(String(e?.message || e)))
            .finally(() => setLoading(false));
    }, [open, idDetalle]);

    return (
        <Drawer
            anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: {
                width: { xs: '100%', md: 720 },
                top: APP_BAR_TOP, height: APP_BAR_HEIGHT_CALC,
            } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header púrpura prominente */}
                <Box sx={{
                    p: 2, bgcolor: COLOR, color: 'white',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    position: 'sticky', top: 0, zIndex: 10,
                }}>
                    <Box sx={{ color: 'white', display: 'flex', '& svg': { fontSize: 28 } }}>
                        <ImportIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                            Detalle del Plan
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                            {d
                                ? `Sol. ${d.ref_nro_plan} · Cuota ${d.Num_Cuota || 1} · Cert. ${d.letra}-${d.numero}`
                                : 'Cargando…'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1, bgcolor: '#f6f8fc' }}>
                    {loading && (
                        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {d && (
                        <>
                            {/* Banda de timeline: 5 etapas */}
                            <TimelineCiclo d={d} />

                            {/* Importación DGR */}
                            <Seccion icon={<ImportIcon />} titulo="① Importación DGR" color="#1565c0">
                                <Linea label="Fecha del lote" valor={fmtFecha(d.Fecha_Lote)} />
                                <Linea label="Importado por" valor={d.Lote_Usuario || '—'} />
                                <Linea label="Fecha de pago" valor={fmtFecha(d.fpago)} />
                                <Linea label="Cuota" valor={`${d.Num_Cuota || 1} de ${d.Total_Cuotas_Honorarios || '—'}`} />
                                <Linea label="Demandado" valor={d.Demandado_NN || '—'} />
                                {d.Demandado_Dom && <Linea label="Domicilio" valor={d.Demandado_Dom} />}
                            </Seccion>

                            {/* Montos */}
                            <Seccion icon={<CobranzaIcon />} titulo="Montos del plan" color="#37474f">
                                <Linea label="Deuda certificado" valor={`$ ${fmtMonto(d.deuda_certificado)}`} />
                                <Linea label="Costas" valor={`$ ${fmtMonto(d.costas)}`} />
                                <Linea label="IVA costas" valor={`$ ${fmtMonto(d.costas_iva)}`} />
                                <Linea label="Gastos" valor={`$ ${fmtMonto(d.gastos)}`} />
                                <Linea label="Pagado" valor={d.pagado || '—'} />
                            </Seccion>

                            {/* Conciliación BSJ/RA */}
                            <Seccion icon={<ConciliacionIcon />} titulo="② Conciliación bancaria (BSJ / RA)"
                                color={d.Conciliacion_Estado ? (ESTADO_CONC[d.Conciliacion_Estado]?.color || '#666') : '#666'}>
                                {d.Conciliacion_Estado ? (
                                    <>
                                        <Box sx={{ mb: 1 }}>
                                            <Chip
                                                label={ESTADO_CONC[d.Conciliacion_Estado]?.label || d.Conciliacion_Estado}
                                                sx={{
                                                    bgcolor: ESTADO_CONC[d.Conciliacion_Estado]?.bg,
                                                    color: ESTADO_CONC[d.Conciliacion_Estado]?.color,
                                                    fontWeight: 700,
                                                }}
                                            />
                                            {d.Conciliacion_Origen && (
                                                <Chip
                                                    label={`Origen: ${d.Conciliacion_Origen}${d.Conciliacion_SubOrigen ? ' / ' + d.Conciliacion_SubOrigen : ''}`}
                                                    size="small" variant="outlined" sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Box>
                                        {(Number(d.BSJ_Honorarios) > 0 || Number(d.BSJ_Gastos) > 0) && (
                                            <Linea label="BSJ — Hon / Gas"
                                                valor={`$ ${fmtMonto(d.BSJ_Honorarios)} / $ ${fmtMonto(d.BSJ_Gastos)}`} />
                                        )}
                                        {(Number(d.RA_Honorarios) > 0 || Number(d.RA_Gastos) > 0) && (
                                            <Linea label="RA — Hon / Gas"
                                                valor={`$ ${fmtMonto(d.RA_Honorarios)} / $ ${fmtMonto(d.RA_Gastos)}`} />
                                        )}
                                        {d.Conciliacion_Fecha && (
                                            <Linea label="Procesado" valor={fmtFecha(d.Conciliacion_Fecha)} />
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Sin registro de conciliación todavía (el plan no se cruzó con BSJ/RA).
                                    </Typography>
                                )}
                            </Seccion>

                            {/* Vinculación a expediente */}
                            <Seccion icon={<VinculacionIcon />} titulo="③ Vinculación a expediente"
                                color={d.Expediente_Id ? '#2e7d32' : '#999'}>
                                {d.Expediente_Id ? (
                                    <>
                                        <Box sx={{ mb: 1 }}>
                                            <Chip icon={<CheckCircleIcon />} label="Vinculado"
                                                sx={{ bgcolor: '#E8F5E9', color: '#2e7d32', fontWeight: 700 }} />
                                            <Tooltip title="Abrir expediente">
                                                <Button size="small" endIcon={<OpenInNewIcon />}
                                                    onClick={() => navigate(`/expedientes/${d.Expediente_Id}`)}
                                                    sx={{ ml: 1, textTransform: 'none' }}>
                                                    Exp. {d.Expediente_Id}
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                        <Linea label="Caratula" valor={d.Exp_Caratula || '—'} />
                                        <Linea label="Autos"
                                            valor={d.Exp_Autos_Vivo || d.Expediente_Autos_Persistido || '—'} />
                                        {(d.Juzgado_Nombre || d.Jurisdiccion_Nombre || d.Fuero_Nombre) && (
                                            <Linea label="Radicación"
                                                valor={[d.Juzgado_Nombre, d.Fuero_Nombre, d.Jurisdiccion_Nombre].filter(Boolean).join(' · ')} />
                                        )}
                                        {d.Exp_Estado_Nombre && (
                                            <Linea label="Estado expediente" valor={d.Exp_Estado_Nombre} />
                                        )}
                                        <Linea label="Apoderado activo"
                                            valor={d.Abogado_Apoderado_NN
                                                ? `${d.Abogado_Apoderado_NN}${d.Abogado_Apoderado_Mat ? ' · Mat. ' + d.Abogado_Apoderado_Mat : ''}`
                                                : d.Abogado_NN || '—'} />
                                    </>
                                ) : (
                                    <Box>
                                        <Chip icon={<PendingIcon />} label="Sin expediente"
                                            sx={{ bgcolor: '#FFF3E0', color: '#e65100', fontWeight: 700 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            El plan no está vinculado a un expediente. El cron diario intenta vincularlo
                                            automáticamente cada noche. Podés forzarlo desde "Planes (DGR)" → "Vincular ahora".
                                        </Typography>
                                    </Box>
                                )}
                            </Seccion>

                            {/* Auditoría */}
                            <Seccion icon={<AuditoriaIcon />} titulo="④ Auditoría"
                                color={d.Auditado === 1 ? '#2e7d32' : (d.Auditado === -1 ? '#c62828' : '#e65100')}>
                                {d.Auditado === 1 && (
                                    <Chip icon={<CheckCircleIcon />} label="Auditado"
                                        sx={{ bgcolor: '#E8F5E9', color: '#2e7d32', fontWeight: 700 }} />
                                )}
                                {d.Auditado === 0 && (
                                    <Chip icon={<PendingIcon />} label="Pendiente de auditar"
                                        sx={{ bgcolor: '#FFF3E0', color: '#e65100', fontWeight: 700 }} />
                                )}
                                {d.Auditado === -1 && (
                                    <Chip icon={<ErrorIcon />} label="Incompleto"
                                        sx={{ bgcolor: '#FFEBEE', color: '#c62828', fontWeight: 700 }} />
                                )}
                                {d.Fecha_Ultima_Actualizacion && (
                                    <Linea label="Última actualización" valor={fmtFecha(d.Fecha_Ultima_Actualizacion)} />
                                )}
                                <Linea label="Resultado cruzamiento"
                                    valor={d.Resultado_Cruzamiento === 1 ? 'Coincide con certificado interno'
                                        : d.Resultado_Cruzamiento === -1 ? 'Sin coincidencia (incompleto)'
                                        : 'Sin cruzar'} />
                                {d.Resultado_Cadena && (
                                    <Linea label="Estado interno" valor={d.Resultado_Cadena} />
                                )}
                                <Box sx={{ mt: 1 }}>
                                    <Button size="small" endIcon={<OpenInNewIcon />}
                                        onClick={() => { onClose?.(); navigate('/auditoria'); }}
                                        sx={{ textTransform: 'none' }}>
                                        Ir a Auditoría
                                    </Button>
                                </Box>
                            </Seccion>

                            {/* Cobranza */}
                            <Seccion icon={<CobranzaIcon />} titulo="⑤ Cobranza de honorarios" color="#37474f">
                                {Number(d.Solicitudes_Total) > 0 ? (
                                    <>
                                        <Chip
                                            label={`${d.Solicitudes_Total} solicitud(es) de pago en este expediente`}
                                            sx={{ bgcolor: '#E8F5E9', color: '#2e7d32', fontWeight: 700 }}
                                        />
                                        <Box sx={{ mt: 1 }}>
                                            <Button size="small" endIcon={<OpenInNewIcon />}
                                                onClick={() => navigate('/honorarios/admin')}
                                                sx={{ textTransform: 'none' }}>
                                                Ver solicitudes en Honorarios
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Aún no se generó ninguna solicitud de pago para este expediente.
                                    </Typography>
                                )}
                            </Seccion>
                        </>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
}

// ----------------------------------------------------------------------------
// Subcomponentes
// ----------------------------------------------------------------------------

function TimelineCiclo({ d }) {
    // 5 hitos: import, conciliación, vinculación, auditoría, cobranza
    const hitos = [
        { label: 'Importado', done: !!d.fpago, color: '#1565c0' },
        {
            label: 'Conciliado',
            done: d.Conciliacion_Estado === 'V',
            warn: d.Conciliacion_Estado && d.Conciliacion_Estado !== 'V',
            color: '#1565c0',
        },
        {
            label: 'Vinculado',
            done: !!d.Expediente_Id,
            color: '#2e7d32',
        },
        {
            label: 'Auditado',
            done: d.Auditado === 1,
            warn: d.Auditado === -1,
            color: '#37474f',
        },
        {
            label: 'Cobrado',
            done: Number(d.Solicitudes_Total) > 0,
            color: '#37474f',
        },
    ];
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px` }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="nowrap">
                {hitos.map((h, i) => (
                    <React.Fragment key={i}>
                        <Stack alignItems="center" sx={{ minWidth: 60 }}>
                            <Box sx={{
                                width: 28, height: 28, borderRadius: '50%',
                                bgcolor: h.done ? '#2e7d32' : (h.warn ? '#e65100' : '#e0e0e0'),
                                color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: 14,
                            }}>
                                {h.done ? '✓' : (h.warn ? '!' : (i + 1))}
                            </Box>
                            <Typography variant="caption" sx={{
                                mt: 0.5,
                                color: h.done ? '#2e7d32' : (h.warn ? '#e65100' : '#999'),
                                fontWeight: h.done || h.warn ? 700 : 500,
                                fontSize: 11, textAlign: 'center',
                            }}>
                                {h.label}
                            </Typography>
                        </Stack>
                        {i < hitos.length - 1 && (
                            <Box sx={{
                                flexGrow: 1, height: 2,
                                bgcolor: h.done ? '#2e7d32' : '#e0e0e0',
                                mx: 0.5, mt: -2,
                            }} />
                        )}
                    </React.Fragment>
                ))}
            </Stack>
        </Paper>
    );
}

function Seccion({ icon, titulo, color, children }) {
    return (
        <Paper variant="outlined" sx={{
            p: 1.5, mb: 1.5, borderRadius: `${BR}px`,
            borderLeft: `4px solid ${color}`,
        }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444' }}>
                    {titulo}
                </Typography>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <Box>{children}</Box>
        </Paper>
    );
}

function Linea({ label, valor }) {
    return (
        <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            py: 0.5, gap: 1, fontSize: '0.83rem',
        }}>
            <Typography variant="caption" sx={{ color: '#666', minWidth: 130 }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word', flexGrow: 1 }}>
                {valor}
            </Typography>
        </Box>
    );
}
